import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wallet, X } from "lucide-react";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";

/**
 * Compact modal launched from a SpendingShiftCard row. Pre-fills the monthly
 * cap with the previous-month baseline (rounded to nearest 5), and PUTs
 * instead of POSTing if a budget already exists for {category, current month}.
 *
 *   category    — string, e.g. "Transport"
 *   suggested   — number, the previous-month amount (used when no cap yet)
 *   currency    — currency code, only for display
 *   onClose     — user closed without saving
 *   onSaved     — successful save; caller can refresh
 */
export default function BudgetCapModal({ category, suggested, currency, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [existingId, setExistingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const ym = new Date().toISOString().slice(0, 7);

  // Load current-month budget for this category so we know whether to POST
  // (create) or PUT (update). Also pre-fills the input with the existing cap.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/budgets");
        if (cancelled) return;
        const existing = (data || []).find(b => b.category === category && b.month === ym);
        if (existing) {
          setExistingId(existing.id);
          setAmount(String(existing.monthly_limit));
        } else {
          // Round suggested to nearest 5 for a friendlier default cap
          const rounded = Math.max(5, Math.round(Number(suggested || 0) / 5) * 5);
          setAmount(rounded > 0 ? String(rounded) : "");
        }
      } catch (e) {
        console.debug("budget prefill failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [category, ym, suggested]);

  const save = async (e) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!(parsed > 0)) {
      toast.error("Enter an amount greater than zero.");
      return;
    }
    setSaving(true);
    try {
      const payload = { category, monthly_limit: parsed, month: ym };
      if (existingId) {
        await api.put(`/budgets/${existingId}`, payload);
        toast.success(`Updated ${category} cap`);
      } else {
        await api.post("/budgets", payload);
        toast.success(`${category} capped at ${formatMoney(parsed, currency)}`);
      }
      onSaved?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not save the cap";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
      data-testid="budget-cap-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-sm rounded-xl shadow-2xl"
        style={{ background: "var(--bg-card)", border: "1px solid var(--border-default)" }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-default)]">
          <div className="flex items-center gap-2">
            <Wallet size={16} style={{ color: "var(--accent-primary)" }} />
            <h3 className="font-semibold text-[var(--text-primary)]">
              {existingId ? `Update ${category} cap` : `Set a ${category} cap`}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            aria-label="Close"
            data-testid="budget-cap-close"
          >
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="px-5 py-8 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
            <Loader2 size={14} className="animate-spin" /> Loading current budget…
          </div>
        ) : (
          <form onSubmit={save} className="px-5 py-4 grid gap-4">
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              {existingId
                ? `You already have a cap for ${category} this month. Adjust it below.`
                : `You spent ${formatMoney(suggested, currency)} on ${category} last month. Cap it at this amount to keep next month in check.`}
            </p>
            <label className="grid gap-1.5">
              <span className="text-xs font-medium text-[var(--text-secondary)]">
                Monthly cap ({currency})
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                autoFocus
                className="field-input"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                data-testid="budget-cap-amount-input"
              />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                className="btn-ghost"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
                data-testid="budget-cap-save"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                {existingId ? "Update cap" : "Set cap"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
